/**
 * Helpers for the on-the-fly image resize proxy (functions/utilities/imageProxy.js,
 * served via the Firebase Hosting `/img` rewrite).
 *
 * resizedUrl(url, width)  -> a CDN-cached resized URL, or the original when the
 *                            proxy can't help (SVGs, external images, local dev).
 * srcSet(url, widths)     -> a responsive srcset string for the given widths.
 * vuetifySrc(url, opts)   -> a Vuetify <v-img> `src` value (string or {src,srcset,lazySrc}).
 */

import { isPrerenderBuild } from '@/utils/prerender'

// Keep in sync with ALLOWED_WIDTHS in functions/utilities/imageProxy.js
const ALLOWED_WIDTHS = [200, 400, 600, 800, 1200, 1600, 2000]
const DEFAULT_SRCSET_WIDTHS = [400, 800, 1200, 1600, 2000]

function snapWidth(width) {
  return ALLOWED_WIDTHS.find((w) => w >= width) || ALLOWED_WIDTHS[ALLOWED_WIDTHS.length - 1]
}

function isOurStorage(url) {
  return (
    typeof url === 'string' &&
    url.includes('firebasestorage.googleapis.com') &&
    (url.includes('renedian-production') || url.includes('renedian-development'))
  )
}

function isSvg(url) {
  return typeof url === 'string' && /\.svg($|\?)/i.test(url)
}

/**
 * The /img rewrite + imageProxy function only exist once deployed to Firebase
 * Hosting. This stays OFF until:
 *   1. `firebase deploy --only functions:imageProxy,hosting` has run, AND
 *   2. VUE_APP_IMAGE_PROXY=true is set and the app is rebuilt.
 * Until then (and always in local dev / prerender) we pass through the original
 * URLs so images never break.
 */
function proxyEnabled() {
  if (process.env.VUE_APP_IMAGE_PROXY !== 'true') return false
  if (isPrerenderBuild()) return false
  if (typeof window === 'undefined') return false
  const host = window.location.host || ''
  return (
    host.includes('renedian.com') ||
    host.endsWith('.web.app') ||
    host.endsWith('.firebaseapp.com')
  )
}

export function canProxy(url) {
  return isOurStorage(url) && !isSvg(url) && proxyEnabled()
}

export function resizedUrl(originalUrl, width = 800, { quality } = {}) {
  if (!canProxy(originalUrl)) return originalUrl
  const w = snapWidth(width)
  const q = quality ? `&q=${quality}` : ''
  return `/img?src=${encodeURIComponent(originalUrl)}&w=${w}${q}`
}

export function srcSet(originalUrl, widths = DEFAULT_SRCSET_WIDTHS) {
  if (!canProxy(originalUrl)) return ''
  return widths
    .map((w) => `${resizedUrl(originalUrl, w)} ${w}w`)
    .join(', ')
}

/**
 * Build a Vuetify v-img `src` value. When the proxy is available it returns an
 * object with a responsive `srcset` (browser default `sizes` is 100vw, which is
 * correct for full-bleed heroes) plus a tiny `lazySrc` placeholder. Otherwise it
 * returns the original URL string unchanged.
 */
export function vuetifySrc(originalUrl, { fallbackWidth = 1200, widths } = {}) {
  if (!canProxy(originalUrl)) return originalUrl
  return {
    src: resizedUrl(originalUrl, fallbackWidth),
    srcset: srcSet(originalUrl, widths),
    lazySrc: resizedUrl(originalUrl, 200),
  }
}
